import FormBuilderOb from '@components/FormBuilder/FormBuilder.ob';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { Patient, usePatientStore } from '@store/patientStore';
import { FormIds } from '@utils/enums/FormIds.enum';
import { ErrorMessage, Loader } from '@gemini/lux-ds-pms';

import useGetCountryList from '@hooks/useGetCountryList';
import useGetCountryStatesList from '@hooks/useGetCountryStatesList';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useGetHoldersList from '@hooks/useGetHoldersList';
import useGetGenderList from '@hooks/useGetGenderList';
import { MultiplePatientsNavigator } from '@components/MultiplePatientsNavigator/MultiplePatientsNavigator';
import { NewPatientSection } from '@components/NewPatientSection/NewPatientSection';
import { Helmet } from 'react-helmet';
import { t } from 'i18next';
import useContinueValidationFeedback from '@hooks/useContinueValidationFeedback';
import useAnalytics from '@hooks/useAnalytics';
import { FlowsPages } from '@utils/enums/FlowsPages.enum';
import { StaticValues } from '@utils/enums/StaticValues.enum';
import { StepsStep } from '@utils/enums/StepsStep.enum';
import { MobileBreakpoint } from '@utils/consts/breakpoints.consts';
import { StepContainer } from '@components/Steps/Steps.styles';
import { CardTitleWrapper } from '@components/BookingDetails/BookingDetails.styles';
import {
  Box,
  LuxRadioButton,
  LuxTypography,
  useMediaQuery,
  useTheme,
} from '@gemini/fe-ds-core';
import MultiplePatientsTabs from '@components/MultiplePatietnsTabs/MultiplePatientsTabs';
import InsuranceFormComponent from '@components/InsurancesForm/PatientDetailsInsuranceForm/PatientDetailsInsuranceForm.component';
import { InsuranceFormType } from '@components/InsurancesForm/PatientDetailsInsuranceForm/PatientDetailsInsuranceForm.interface';
import CustomInsuranceFieldsForm from '@components/InsurancesForm/CustomInsuranceFieldsForm/CustomInsuranceFieldsForm.component';
import { hasVisibleCustomInsuranceFields } from '@components/InsurancesForm/CustomInsuranceFieldsForm/CustomInsuranceFieldsForm.utils';
import {
  buildInitialPatientDetailsEnableList,
  buildPatientDetailsFormDataOnUnmount,
  buildPatientDetailsFormIdsList,
  buildPatientDetailsFormOptionsList,
  getCurrentPatientValidationState,
  getIncompletePatientNames as getIncompletePatientNamesForStep,
  hasAnyError,
  hasAnyLoading,
  getSecondaryInsurancePlanAnswer,
  isPatientDetailsStepComplete,
  isPrimaryInsurancePlanRequired,
  isSecondaryInsurancePlanRequired,
  PatientDetailsInsuranceRules,
  PatientDetailsValidationLists,
  shouldShowInsuranceForms as getShouldShowInsuranceForms,
  shouldShowInsuranceSection as getShouldShowInsuranceSection,
  shouldShowPrimaryInsurancePlanError as getShouldShowPrimaryInsurancePlanError,
  shouldShowPrimaryInsurancePlanQuestion,
  shouldShowSecondaryInsuranceForm as getShouldShowSecondaryInsuranceForm,
  shouldShowSecondaryInsurancePlanError as getShouldShowSecondaryInsurancePlanError,
  shouldShowSecondaryInsurancePlanQuestion,
  updateIndexedBooleanList,
  updatePatientDetailsEnableList,
} from './PatientDetailsPage.utils';

interface PatientDetailsProps {
  setIsStepValid: (val: boolean) => void;
}

const PatientDetailsPage = ({ setIsStepValid }: PatientDetailsProps) => {
  // #region Hooks

  const { trackPageAnalytics } = useAnalytics();
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${MobileBreakpoint}px)`);

  const showAddNewPatientFrame = useOnlineBookingStore(
    state => state.showAddPatientFrame,
  );
  const currentPatientIndex = useOnlineBookingStore(
    state => state.currentPatient,
  );
  const formConfigs = useOnlineBookingStore(state => state.formConfigs);
  const brandName = useOnlineBookingStore(state => state.brandName);
  const organizationId = useOnlineBookingStore(
    state => state.generalInfo?.organizationId,
  );
  const readOnlyPrefilledPatientsDetailsFields = useOnlineBookingStore(
    state => state.readOnlyPrefilledPatientsDetailsFields,
  );
  const infoUser = useOnlineBookingStore(state => state.infoUser);

  const updatePatient = usePatientStore(state => state.updatePatient);
  const patients = usePatientStore(state => state.patients);
  const [primaryInsuranceValid, setPrimaryInsuranceValid] = useState<boolean[]>(
    [],
  );
  const [secondaryInsuranceValid, setSecondaryInsuranceValid] = useState<
    boolean[]
  >([]);
  const [customInsuranceFieldsValid, setCustomInsuranceFieldsValid] = useState<
    boolean[]
  >([]);
  const [insurancePlan, setInsurancePlan] = useState<string[]>(
    patients?.map(p => p?.insurance?.plan || '') || [],
  );
  const insurancePlanRef = useRef(insurancePlan);
  const [secondaryInsuranceAnswer, setSecondaryInsuranceAnswer] = useState<
    string[]
  >(patients?.map(getSecondaryInsurancePlanAnswer) || []);
  const secondaryInsuranceAnswerRef = useRef(secondaryInsuranceAnswer);
  const secondaryInsuranceFormVisibleOnSubmitRef = useRef<boolean[]>([]);

  const {
    data: genderList,
    isLoading: isGenderLoading,
    isError: isErrorInGender,
  } = useGetGenderList({
    organizationId,
  });

  const {
    data: holders,
    isLoading: isHoldersLoading,
    isError: isErrorInHolders,
  } = useGetHoldersList({
    organizationId,
  });

  const {
    data: countryList,
    isLoading: isCountryListLoading,
    isError: isErrorInCountryList,
  } = useGetCountryList();

  const {
    data: countryStatesList,
    isLoading: isCountryStatesListLoading,
    isError: isErrorInCountryStatesList,
  } = useGetCountryStatesList({ countryCode: 'US' });

  const [listEnableButton, setListEnableButton] = useState<
    Array<boolean | undefined>
  >([]);

  // #endregion Hooks

  // #region Memos

  const askForInsuranceInformation = useMemo(
    () => !!formConfigs?.askForInsuranceInformation,
    [formConfigs],
  );

  const insuranceDrivesAvailability = useMemo(
    () =>
      !!(
        askForInsuranceInformation &&
        formConfigs?.insuranceMustDriveAvailability
      ),
    [askForInsuranceInformation, formConfigs],
  );

  const showInsuranceInPatientDetails = useMemo(
    () => askForInsuranceInformation && !insuranceDrivesAvailability,
    [askForInsuranceInformation, insuranceDrivesAvailability],
  );

  const primaryInsurancePlanStatus =
    formConfigs?.patientDetails?.primaryInsuranceDetails?.submitInsurancePlan;

  const showPrimaryInsurancePlanQuestion = useMemo(
    () =>
      shouldShowPrimaryInsurancePlanQuestion(
        showInsuranceInPatientDetails,
        primaryInsurancePlanStatus,
      ),
    [primaryInsurancePlanStatus, showInsuranceInPatientDetails],
  );

  const primaryInsurancePlanRequired = useMemo(
    () => isPrimaryInsurancePlanRequired(primaryInsurancePlanStatus),
    [primaryInsurancePlanStatus],
  );

  const secondaryInsurancePlanStatus =
    formConfigs?.patientDetails?.secondaryInsuranceDetails?.submitInsurancePlan;

  const showSecondaryInsurancePlanQuestion = useMemo(
    () =>
      shouldShowSecondaryInsurancePlanQuestion(secondaryInsurancePlanStatus),
    [secondaryInsurancePlanStatus],
  );

  const secondaryInsurancePlanRequired = useMemo(
    () => isSecondaryInsurancePlanRequired(secondaryInsurancePlanStatus),
    [secondaryInsurancePlanStatus],
  );

  const showCustomInsuranceFields = useMemo(
    () =>
      hasVisibleCustomInsuranceFields(
        formConfigs?.patientDetails?.customFieldsInsurance,
      ),
    [formConfigs],
  );

  const insuranceRules = useMemo<PatientDetailsInsuranceRules>(
    () => ({
      askForInsuranceInformation,
      insuranceDrivesAvailability,
      primaryInsurancePlanRequired,
      secondaryInsurancePlanRequired,
      showCustomInsuranceFields,
      showPrimaryInsurancePlanQuestion,
      showSecondaryInsurancePlanQuestion,
    }),
    [
      askForInsuranceInformation,
      insuranceDrivesAvailability,
      primaryInsurancePlanRequired,
      secondaryInsurancePlanRequired,
      showCustomInsuranceFields,
      showPrimaryInsurancePlanQuestion,
      showSecondaryInsurancePlanQuestion,
    ],
  );

  const validationLists = useMemo<PatientDetailsValidationLists>(
    () => ({
      customInsuranceFieldsValid,
      insurancePlan,
      listEnableButton,
      primaryInsuranceValid,
      secondaryInsuranceAnswer,
      secondaryInsuranceValid,
    }),
    [
      customInsuranceFieldsValid,
      insurancePlan,
      listEnableButton,
      primaryInsuranceValid,
      secondaryInsuranceAnswer,
      secondaryInsuranceValid,
    ],
  );

  const shouldShowInsuranceSection = useCallback(
    (patient?: Patient) =>
      getShouldShowInsuranceSection({ patient, rules: insuranceRules }),
    [insuranceRules],
  );

  const shouldShowInsuranceForms = useCallback(
    (patient: Patient, index: number) =>
      getShouldShowInsuranceForms({
        patient,
        index,
        insurancePlan,
        insuranceDrivesAvailability,
      }),
    [insuranceDrivesAvailability, insurancePlan],
  );

  const shouldShowSecondaryInsuranceForm = useCallback(
    (patient: Patient, index: number) =>
      getShouldShowSecondaryInsuranceForm({
        patient,
        index,
        secondaryInsuranceAnswer,
      }),
    [secondaryInsuranceAnswer],
  );

  const isPatientDetailsStepValid = useMemo(
    () =>
      isPatientDetailsStepComplete({
        patients,
        rules: insuranceRules,
        validationLists,
      }),
    [insuranceRules, patients, validationLists],
  );

  const { currentSecondaryInsuranceFormVisible } = useMemo(
    () =>
      getCurrentPatientValidationState({
        currentPatientIndex,
        patients,
        rules: insuranceRules,
        validationLists,
      }),
    [currentPatientIndex, insuranceRules, patients, validationLists],
  );

  const getIncompletePatientNames = useCallback(
    () =>
      getIncompletePatientNamesForStep({
        currentPatientIndex,
        fallbackPatientName: t('detailsSection.newPatient'),
        patients,
        rules: insuranceRules,
        validationLists,
      }),
    [currentPatientIndex, insuranceRules, patients, validationLists],
  );

  const {
    submitAttemptCount: newPatientSubmitAttemptCount,
    resetSubmitAttemptCount: resetNewPatientSubmitAttemptCount,
  } = useContinueValidationFeedback({
    step: StepsStep.PATIENT_DETAILS,
    enabled: showAddNewPatientFrame,
    resetWhen: !showAddNewPatientFrame,
  });

  const { submitAttemptCount: patientDetailsSubmitAttemptCount } =
    useContinueValidationFeedback({
      step: StepsStep.PATIENT_DETAILS,
      enabled: !showAddNewPatientFrame,
      resetWhen: !showAddNewPatientFrame && isPatientDetailsStepValid,
      getIncompletePatientNames,
      onAttempt: () => {
        secondaryInsuranceFormVisibleOnSubmitRef.current[currentPatientIndex] =
          currentSecondaryInsuranceFormVisible;
      },
    });

  const shouldShowPrimaryInsurancePlanError = useCallback(
    (patient: Patient, index: number) =>
      getShouldShowPrimaryInsurancePlanError({
        currentPatientIndex,
        index,
        patient,
        submitAttemptCount: patientDetailsSubmitAttemptCount,
        rules: insuranceRules,
        validationLists,
      }),
    [
      currentPatientIndex,
      insuranceRules,
      patientDetailsSubmitAttemptCount,
      validationLists,
    ],
  );

  const shouldShowSecondaryInsurancePlanError = useCallback(
    (patient: Patient, index: number) =>
      getShouldShowSecondaryInsurancePlanError({
        currentPatientIndex,
        index,
        patient,
        submitAttemptCount: patientDetailsSubmitAttemptCount,
        rules: insuranceRules,
        validationLists,
      }),
    [
      currentPatientIndex,
      insuranceRules,
      patientDetailsSubmitAttemptCount,
      validationLists,
    ],
  );

  const patientDetailsFormIdsList = useMemo(
    () =>
      buildPatientDetailsFormIdsList({
        patients,
        showCustomFieldsPatient:
          !!formConfigs?.patientDetails.customFieldsPatient?.show,
      }),
    [formConfigs, patients],
  );

  const patientDetailsFormOptionsList = useMemo(
    () =>
      buildPatientDetailsFormOptionsList({
        countryList,
        countryStatesList,
        genderList,
        infoUser,
        patients,
        readOnlyPrefilledPatientsDetailsFields:
          !!readOnlyPrefilledPatientsDetailsFields,
        showCustomFieldsPatient:
          !!formConfigs?.patientDetails.customFieldsPatient?.show,
      }),
    [
      countryList,
      countryStatesList,
      formConfigs,
      genderList,
      infoUser,
      patients,
      readOnlyPrefilledPatientsDetailsFields,
    ],
  );

  const isLoading = useMemo(
    () =>
      hasAnyLoading(
        isCountryListLoading,
        isCountryStatesListLoading,
        isHoldersLoading,
        isGenderLoading,
      ),
    [
      isCountryListLoading,
      isCountryStatesListLoading,
      isGenderLoading,
      isHoldersLoading,
    ],
  );

  const isError = useMemo(
    () =>
      hasAnyError(
        isErrorInCountryList,
        isErrorInCountryStatesList,
        isErrorInGender,
        isErrorInHolders,
      ),
    [
      isErrorInCountryList,
      isErrorInCountryStatesList,
      isErrorInGender,
      isErrorInHolders,
    ],
  );

  const patientsPresenceKey = useMemo(
    () => patients.map(p => (p ? '1' : '0')).join('|'),
    [patients],
  );

  // #endregion Memos

  // #region Functions

  const handleEnableButton = useCallback((index: number, enable: boolean) => {
    setListEnableButton(prev =>
      updatePatientDetailsEnableList({
        currentList: prev,
        index,
        value: enable,
      }),
    );
  }, []);

  const handleValidatePrimaryInsurance = useCallback(
    (isValid: boolean, index: number) => {
      setPrimaryInsuranceValid(prev =>
        updateIndexedBooleanList({
          currentList: prev,
          index,
          value: isValid,
        }),
      );
    },
    [],
  );

  const handleValidateSecondaryInsurance = useCallback(
    (isValid: boolean, index: number) => {
      setSecondaryInsuranceValid(prev =>
        updateIndexedBooleanList({
          currentList: prev,
          index,
          value: isValid,
        }),
      );
    },
    [],
  );

  const handleValidateCustomInsuranceFields = useCallback(
    (isValid: boolean, index: number) => {
      setCustomInsuranceFieldsValid(prev =>
        updateIndexedBooleanList({
          currentList: prev,
          index,
          value: isValid,
        }),
      );
    },
    [],
  );

  const handleNewPatientEnableButton = useCallback(
    (enable: boolean) => {
      setIsStepValid(enable);

      if (enable) {
        resetNewPatientSubmitAttemptCount();
      }
    },
    [resetNewPatientSubmitAttemptCount, setIsStepValid],
  );

  // #endregion Functions

  // #region Effects

  //ANALYTICS (RONA only) - Personal Info Form
  useEffect(() => {
    if (brandName) {
      trackPageAnalytics({
        page: showAddNewPatientFrame
          ? FlowsPages.ADD_NEW_PATIENT
          : FlowsPages.PATIENT_DETAILS,
        patients,
      });
    }
  }, [showAddNewPatientFrame]);

  useEffect(() => {
    if (showAddNewPatientFrame) {
      setIsStepValid(false);
      return;
    }

    setIsStepValid(isPatientDetailsStepValid);
  }, [isPatientDetailsStepValid, setIsStepValid, showAddNewPatientFrame]);

  useEffect(() => {
    setListEnableButton(buildInitialPatientDetailsEnableList(patients));
  }, [patientsPresenceKey]);

  // #endregion Effects

  // #region Constants

  const planList = [
    { label: 'Yes', value: StaticValues.YES },
    { label: 'No', value: StaticValues.NO },
  ];

  // #endregion Constants

  // #region JSX

  return (
    <>
      <Helmet>
        <title>{t('wcag.ob_patient_details_title')}</title>
      </Helmet>
      {showAddNewPatientFrame && (
        <StepContainer
          borderColor={theme.palette.LUX.obTokens?.bordersColorNeutralSecondary}
          style={{
            scrollMarginTop: isMobile ? 200 : 160,
          }}
        >
          <CardTitleWrapper
            borderColor={
              theme.palette.LUX.obTokens?.bordersColorNeutralSecondary
            }
          >
            <LuxTypography variant="heading4" component="h2">
              {t('newPatientPage.title')}
            </LuxTypography>
          </CardTitleWrapper>
          <MultiplePatientsTabs />
          <NewPatientSection
            setEnableButton={handleNewPatientEnableButton}
            submitAttemptCount={newPatientSubmitAttemptCount}
          />
        </StepContainer>
      )}
      {!showAddNewPatientFrame && isLoading && <Loader />}
      {!showAddNewPatientFrame && !isLoading && isError && (
        <ErrorMessage></ErrorMessage>
      )}
      {!showAddNewPatientFrame && !isLoading && !isError && (
        <>
          {patients.map((patient, index) =>
            patient ? (
              <Fragment key={`patientDetailsForms_${index}`}>
                <StepContainer
                  borderColor={
                    theme.palette.LUX.obTokens?.bordersColorNeutralSecondary
                  }
                  style={{
                    display: currentPatientIndex === index ? 'flex' : 'none',
                    scrollMarginTop: isMobile ? 200 : 160,
                  }}
                >
                  <CardTitleWrapper
                    borderColor={
                      theme.palette.LUX.obTokens?.bordersColorNeutralSecondary
                    }
                  >
                    <LuxTypography variant="heading4" component="h2">
                      {t('patientDetailsPage.title')}
                    </LuxTypography>
                  </CardTitleWrapper>
                  <MultiplePatientsTabs />
                  <FormBuilderOb
                    key={`patientDetails_${index}`}
                    isVisible={currentPatientIndex === index}
                    formIds={patientDetailsFormIdsList[index]}
                    setEnableButton={enable =>
                      handleEnableButton(index, enable)
                    }
                    submitAttemptCount={
                      currentPatientIndex === index
                        ? patientDetailsSubmitAttemptCount
                        : 0
                    }
                    onUnmount={formData => {
                      updatePatient(
                        index,
                        FormIds.PatientDetail,
                        buildPatientDetailsFormDataOnUnmount({
                          formData,
                          patient,
                          insurancePlan: insurancePlanRef.current[index],
                          insuranceDrivesAvailability,
                          secondaryInsuranceAnswer:
                            secondaryInsuranceAnswerRef.current[index],
                          showSecondaryInsurancePlanQuestion,
                        }),
                      );
                    }}
                    optionsList={patientDetailsFormOptionsList[index]}
                    patientIndex={index}
                    guardClickableAnnouncement
                    mykey={`patientDetails_${index}`}
                  />
                </StepContainer>
                {shouldShowInsuranceSection(patient) && (
                  <StepContainer
                    marginTop="1.5rem"
                    borderColor={
                      theme.palette.LUX.obTokens?.bordersColorNeutralSecondary
                    }
                    style={{
                      display: currentPatientIndex === index ? 'flex' : 'none',
                      scrollMarginTop: isMobile ? 200 : 160,
                    }}
                  >
                    <CardTitleWrapper
                      borderColor={
                        theme.palette.LUX.obTokens?.bordersColorNeutralSecondary
                      }
                    >
                      <LuxTypography variant="heading4" component="h2">
                        {t('detailsSection.insuranceDetails')}
                      </LuxTypography>
                    </CardTitleWrapper>
                    {showPrimaryInsurancePlanQuestion && (
                      <Box padding="0.5rem 1rem 1rem 1rem">
                        <LuxRadioButton
                          name={`patientDetailsInsurancePlan_${index}`}
                          direction="horizontal"
                          required={primaryInsurancePlanRequired}
                          standalone
                          label={
                            t(
                              'fieldsLabel.primaryInsuranceDetails.insurancePlan',
                            ) as string
                          }
                          options={planList}
                          value={
                            insurancePlan[index] || patient.insurance?.plan
                          }
                          onChange={v => {
                            const newInsurancePlan = [...insurancePlan];
                            newInsurancePlan[index] = v;
                            insurancePlanRef.current = newInsurancePlan;
                            setInsurancePlan(newInsurancePlan);
                            updatePatient(index, FormIds.Insurance, {
                              insurance: { plan: v },
                              ...(v === StaticValues.NO
                                ? {
                                    patientDetails: {
                                      storeCustomFieldsInsurance: undefined,
                                    },
                                  }
                                : {}),
                            });
                            if (v === StaticValues.NO) {
                              handleValidateCustomInsuranceFields(true, index);
                            }
                            handleValidatePrimaryInsurance(
                              v === StaticValues.NO,
                              index,
                            );
                          }}
                        />{' '}
                        {shouldShowPrimaryInsurancePlanError(
                          patient,
                          index,
                        ) && (
                          <LuxTypography
                            data-ob-scroll-error="true"
                            variant="textXs"
                            style={{
                              color:
                                theme.palette.LUX.obTokens
                                  .textColorFunctionalError,
                            }}
                          >
                            {t('fieldsLabel.errors.isRequired')}
                          </LuxTypography>
                        )}
                      </Box>
                    )}

                    {shouldShowInsuranceForms(patient, index) && (
                      <>
                        <InsuranceFormComponent
                          key={`${InsuranceFormType.PRIMARY}_${index}`}
                          currentIndex={index}
                          insuranceFormType={InsuranceFormType.PRIMARY}
                          countryStates={countryStatesList}
                          countries={countryList}
                          gender={genderList}
                          holders={holders}
                          defaultInsuranceID={patient?.insurance?.id}
                          defaultInsuranceNameOther={patient?.insurance?.other}
                          submitAttemptCount={
                            currentPatientIndex === index
                              ? patientDetailsSubmitAttemptCount
                              : 0
                          }
                          onValidate={handleValidatePrimaryInsurance}
                        />
                        {showSecondaryInsurancePlanQuestion && (
                          <Box padding="0.5rem 1rem 1rem 1rem">
                            <LuxRadioButton
                              name={`patientDetailsSecondaryInsurancePlan_${index}`}
                              direction="horizontal"
                              required={secondaryInsurancePlanRequired}
                              standalone
                              label={
                                t(
                                  'fieldsLabel.secondaryInsuranceDetails.insurancePlan',
                                ) as string
                              }
                              options={planList}
                              value={
                                secondaryInsuranceAnswer[index] ||
                                getSecondaryInsurancePlanAnswer(patient)
                              }
                              onChange={v => {
                                const newSecondaryInsuranceAnswer = [
                                  ...secondaryInsuranceAnswer,
                                ];
                                newSecondaryInsuranceAnswer[index] = v;
                                secondaryInsuranceAnswerRef.current =
                                  newSecondaryInsuranceAnswer;
                                setSecondaryInsuranceAnswer(
                                  newSecondaryInsuranceAnswer,
                                );
                                handleValidateSecondaryInsurance(
                                  v === StaticValues.NO,
                                  index,
                                );
                              }}
                            />
                            {shouldShowSecondaryInsurancePlanError(
                              patient,
                              index,
                            ) && (
                              <LuxTypography
                                data-ob-scroll-error="true"
                                variant="textXs"
                                style={{
                                  color:
                                    theme.palette.LUX.obTokens
                                      .textColorFunctionalError,
                                }}
                              >
                                {t('fieldsLabel.errors.isRequired')}
                              </LuxTypography>
                            )}
                          </Box>
                        )}
                        {showSecondaryInsurancePlanQuestion &&
                          shouldShowSecondaryInsuranceForm(patient, index) && (
                            <InsuranceFormComponent
                              key={`${InsuranceFormType.SECONDARY}_${index}`}
                              currentIndex={index}
                              insuranceFormType={InsuranceFormType.SECONDARY}
                              countryStates={countryStatesList}
                              countries={countryList}
                              gender={genderList}
                              holders={holders}
                              defaultInsuranceID={undefined}
                              submitAttemptCount={
                                currentPatientIndex === index &&
                                secondaryInsuranceFormVisibleOnSubmitRef
                                  .current[index]
                                  ? patientDetailsSubmitAttemptCount
                                  : 0
                              }
                              onValidate={handleValidateSecondaryInsurance}
                            />
                          )}
                        {showCustomInsuranceFields && (
                          <CustomInsuranceFieldsForm
                            key={`customInsuranceFields_${index}`}
                            currentIndex={index}
                            customFields={
                              formConfigs?.patientDetails?.customFieldsInsurance
                            }
                            initialValues={
                              patient.patientDetails?.storeCustomFieldsInsurance
                            }
                            isVisible={currentPatientIndex === index}
                            submitAttemptCount={
                              currentPatientIndex === index
                                ? patientDetailsSubmitAttemptCount
                                : 0
                            }
                            onValidate={handleValidateCustomInsuranceFields}
                            onChange={formData =>
                              updatePatient(
                                index,
                                FormIds.PatientDetail,
                                formData,
                              )
                            }
                            onCommit={formData =>
                              updatePatient(
                                index,
                                FormIds.PatientDetail,
                                formData,
                              )
                            }
                          />
                        )}
                      </>
                    )}
                  </StepContainer>
                )}
              </Fragment>
            ) : null,
          )}
        </>
      )}
      {!showAddNewPatientFrame && (
        <MultiplePatientsNavigator
          style={{ padding: '0rem 0rem 1.5rem 0rem' }}
        />
      )}
    </>
  );

  // #endregion JSX
};

export default PatientDetailsPage;
